// @flow strict-local

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import compareAsc from 'date-fns/compareAsc';
import parseISO from 'date-fns/parseISO';

import useSchedulerEvent from './useSchedulerEvent';
import * as api from 'src/utils/api-no-store';
import {formatDateTime, getLinkOwner, getLinkOwnerLanguage} from './utils';
import ActionPrompt from './action-prompt.jsx';

import {Button} from 'src/components/lib/new-button';

import {useIsMobile} from '../useIsMobile';
import {useTemplateAvailability} from './use-template-availability.js';
import classify from '@spaced-out/ui-design-system/lib/utils/classify';
import {isLanguageSupported} from 'src/utils/language-support';

import {
  DesktopSchedulingDatePicker,
  MobileSchedulingDatePicker,
} from './date-pickers.jsx';
import {
  Container,
  SuccessPage,
  SuccessfulRescheduleRequestPage,
  NotFoundPage,
  BookingBody,
  TemplateNotFoundPage,
} from './common.jsx';

import css from '../booking.css';


export const RescheduleApp = ({
  eventId,
  timezone,
}: {
  eventId: string,
  timezone: string,
}): React.Node => {
  const isMobile = useIsMobile();
  const eventDataResource = useSchedulerEvent(eventId);
  const {labelI18nInstance} = useI18n();
  const {t, makeKey} = useLabelTranslation();

  const [linkOwner, setLinkOwner] = React.useState<
    'HOST' | 'GUEST' | 'NO_OWNER',
  >('NO_OWNER');
  const [linkOwnerLanguage, setLinkOwnerLanguage] = React.useState<string>(
    labelI18nInstance.language,
  );
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [calendarDate, setCalendarDate] = React.useState(new Date());
  const [firstDatePicked, setFirstDatePicked] = React.useState(false);
  const [rescheduledData, setRescheduledData] = React.useState<
    false | {start_time: string},
  >(false);
  const [reason, setReason] = React.useState('');
  const [rescheduleRequested, setRescheduleRequested] = React.useState(false);
  const [submittingReschedule, setSubmittingReschedule] = React.useState(false);

  const {eventDates, availabilityByDate, isAvailabilityLoading, availability} =
    useTemplateAvailability({
      templateId: eventDataResource.result?.scheduled_from_template_id,
      eventId,
    });

  const sortedDates = [...eventDates].sort(compareAsc);
  const url = new URL(window.location);
  const params = Object.fromEntries(url.searchParams.entries());

  React.useEffect(() => {
    if (!firstDatePicked) {
      if (sortedDates[0] !== undefined) {
        setSelectedDate(sortedDates[0]);
        setCalendarDate(sortedDates[0]);

        setFirstDatePicked(true);
      }
    }
  }, [sortedDates]);

  React.useEffect(() => {
    const updateLanguage = async () => {
      const owner = await getLinkOwner(eventId, params);
      setLinkOwner(owner);

      if (!eventDataResource.result) {
        return;
      }

      const userLanguage = getLinkOwnerLanguage({
        linkOwner: owner,
        userId: params.aid,
        eventData: eventDataResource.result,
      });

      if (
        userLanguage &&
        isLanguageSupported(userLanguage) &&
        userLanguage !== linkOwnerLanguage
      ) {
        setLinkOwnerLanguage(userLanguage);
      }
    };

    updateLanguage();
  }, [eventDataResource.result]);

  const handleRescheduleHost = async (event) => {
    event.preventDefault();
    setSubmittingReschedule(true);
    await api.post(`scheduler/event/${eventId}/reschedule-request`, {
      reason,
    });
    setSubmittingReschedule(false);
    setRescheduleRequested(true);
  };

  const handleRescheduleBooking = React.useCallback(
    async (date: string, start_time: string) => {
      const firstDate = Object.keys(availability || {})[0];
      const firstTimes = availability[firstDate];
      const reschedule = await api.put(`scheduler/event/${eventId}`, {
        date,
        start_time,
        viewer_timezone: timezone,
        attendee: params.attendee,
        first_available_datetime:
          firstTimes && `${firstDate} ${firstTimes[0].start_time}`,
      });
      setRescheduledData({start_time: reschedule.event_start});
    },
    [eventDataResource.result, timezone, availability],
  );

  if (eventDataResource.isLoading) {
    return null;
  }

  const originallyScheduledDate = eventDataResource.result?.event_start
    ? parseISO(eventDataResource.result.event_start)
    : new Date();

  const {brand_settings} = eventDataResource.result || {};

  const formattedDate = formatDateTime(originallyScheduledDate, t, makeKey);

  // TODO (kyle): we should use react to determine what renders -- not css
  return (
    <Container
      brand={brand_settings}
      persistLanguage={false}
      language={linkOwnerLanguage}
    >
      <BookingBody
        className={classify({
          [css.mobileOnly]: isMobile,
          [css.desktopOnly]: !isMobile,
          [css.identificationWrapper]: linkOwner === 'NO_OWNER',
        })}
      >
        {brand_settings == null ? (
          <TemplateNotFoundPage />
        ) : eventDataResource.error ? (
          <NotFoundPage />
        ) : rescheduleRequested ? (
          <SuccessfulRescheduleRequestPage />
        ) : rescheduledData !== false ? (
          <SuccessPage date={parseISO(rescheduledData.start_time)} />
        ) : linkOwner === 'HOST' ? (
          <>
            <h1 className={css.bookingHeader}>
              {t('Reschedule_your_meeting', 'Reschedule your meeting')}
            </h1>
            <h2 className={css.bookingHeader}>
              {`${t('Currently_scheduled_for', 'Currently scheduled for')} `}
              {formattedDate}
            </h2>
            <form className={css.confirmForm} onSubmit={handleRescheduleHost}>
              <label className={css.confirmLabel}>
                <span>{t('Reason', 'Reason')}</span>
                <textarea
                  className={css.multiLineInput}
                  placeholder={t(
                    'Reason_for_rebooking_this_meeting',
                    'Reason for rebooking this meeting',
                  )}
                  value={reason}
                  onChange={(evt) => setReason(evt.currentTarget.value)}
                />
              </label>
              <Button
                type="primary"
                disabled={
                  (submittingReschedule && reason.length > 0) ||
                  rescheduleRequested
                }
                onClick={handleRescheduleHost}
                color={brand_settings.button_color}
                loading={submittingReschedule}
              >
                {t('Request_Reschedule', 'Request Reschedule')}
              </Button>
              <button style={{display: 'none'}} />
            </form>
          </>
        ) : linkOwner === 'GUEST' ? (
          <>
            <h1 className={css.bookingHeader}>
              {t('Reschedule_your_meeting', 'Reschedule your meeting')}
            </h1>
            <h2 className={css.bookingHeader}>
              {`${t('Currently_scheduled_for', 'Currently scheduled for')} `}
              {formattedDate}
            </h2>
            {isMobile ? (
              <div className={css.bookingBox}>
                <MobileSchedulingDatePicker
                  onConfirmTimeslot={handleRescheduleBooking}
                  eventDates={eventDates}
                  availabilityByDate={availabilityByDate}
                  isAvailabilityLoading={isAvailabilityLoading}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  calendarDate={calendarDate}
                  setCalendarDate={setCalendarDate}
                  color={brand_settings.button_color}
                />
              </div>
            ) : (
              <DesktopSchedulingDatePicker
                onConfirmTimeslot={handleRescheduleBooking}
                eventDates={eventDates}
                availabilityByDate={availabilityByDate}
                isAvailabilityLoading={isAvailabilityLoading}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                color={brand_settings.button_color}
              />
            )}
          </>
        ) : (
          <ActionPrompt
            eventId={eventId}
            originallyScheduledDate={originallyScheduledDate}
            event={eventDataResource.result}
            actionType="reschedule"
          />
        )}
      </BookingBody>
    </Container>
  );
};
